<!-- =========================================================================================
  File Name: UserList.vue
  Description: User List page
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>

  <div id="page-product-list" class="ag-grid-container">
    <statistics-cards :data="statisticalData"/>

    <div class="vx-card">
      <div v-show="showGridOne">
        <grid-panel
            height="md"
            :column-defs="columnDefs"
            :url="() => this.commercialProductList"
            auto-size
            @rowClicked="view"
            class="commercial-products-grid"
            show-deleted-toggle
            @update:deleted="getCommercialProducts"
            ref="commercialProductsGrid"
            title="Commercial Products"
        >
          <template v-slot:right>
            <button-toggle
                class="mr-4"
                v-model="toggleValue"
                :options="toggle"
            />
          </template>

          <vs-dropdown-item class="create-commercial-product-action" v-if="componentAccess.canCreate.access">
            <span
                class="flex items-center"
                @click="$router.push({ name: 'commercial-products.create' })"
            >
              <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" class="mr-2" />
              <span>Create Commercial Product</span>
            </span>
          </vs-dropdown-item>
        </grid-panel>
      </div>
      <div v-show="!showGridOne">
        <grid-panel
            height="md"
            :column-defs="producerColumnDefs"
            :gridOptions="producerGridOptions"
            @update:gridApi="(producerGridApi) => this.producerGridApi = producerGridApi"
            :auto-group-column-def="autoGroupColumnDef"
            :data="this.producersList"
            auto-size
            @rowClicked="view"
            class="commercial-products-producer-grid"
            ref="producerGrid"
            title="Commercial Products"
        >
          <template v-slot:right>
            <button-toggle
                class="mr-4"
                v-model="toggleValue"
                :options="toggle"
            />
          </template>

          <vs-dropdown-item v-if="componentAccess.canCreate.access">
            <span
                class="flex items-center"
                @click="$router.push({ name: 'commercial-products.create' })"
            >
              <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" class="mr-2" />
              <span>Create Commercial Product</span>
            </span>
          </vs-dropdown-item>
          <vs-dropdown-item
              v-if="componentAccess.canEdit.access"
          >
            <span
                class="flex items-center"
                @click="copyCommercialProducts"
            >
              <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" class="mr-2" />
              <span>Copy Commercial Products</span>
            </span>
          </vs-dropdown-item>
        </grid-panel>
      </div>
    </div>

    <commercial-product-transfer-modal
        :visible="modals.transferModal.visible"
        :selected-producer="selectedProducer"
        @updated="modalUpdated('transferModal')"
        @close="closeModal('transferModal')"
        @cancel="closeModal('transferModal')"
        @error="modalError"
    />
  </div>

</template>

<script>

import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss';
import CanAccessMixin from "@/components/mixins/CanAccessMixin";

// Cell Renderer
import TGrid from "@/views/components/ag-grid/TGrid";
import CellMatchingMixin from "@/views/pages/sync/CellMatchingMixin";
import GridPanel from "@/views/components/ag-grid/GridPanel";
import YesNoToggle from "@/views/components/extra-components/YesNoToggle";
import StatisticsCards from '@/components/statistics-cards/StatisticsCards.vue'
import CommercialProductTransferModal from "@/views/pages/commercial-products/CommercialProductTransferModal";
import NotificationMixin from "@/components/mixins/NotificationMixin";
import ButtonToggle from "../../components/extra-components/toggle/ButtonToggle";

export default {
  components: {
    TGrid,
    GridPanel,
    YesNoToggle,
    StatisticsCards,
    CommercialProductTransferModal,
    ButtonToggle
  },
  mixins: [CellMatchingMixin,NotificationMixin,CanAccessMixin],
  data () {
    return {
      producerGridApi: null,
      componentAccess: {
        canCreate: {
          route: 'commercial-products.create',
          access: false,
        },
        canEdit: {
          route: 'commercial-products.edit',
          access: false,
        },
      },
      toggle: [
        {
          text: 'None',
          value: 0,
        },
        {
          text: 'By Producer',
          value: 1,
        }
      ],
      toggleValue: 0,
      commercialProductList: {},
      producersList: null,
      searchQuery: '',
      showDeleted: false,
      producerGridOptions: {
        // groupUseEntireRow: true,
        // groupRowRendererParams: {suppressCount: true},
      },
      selectedProducer: 0,

      modals: {
        transferModal: {
          visible: false
        },
      },

      // AgGrid
      columnDefs: [
        {
          headerName: 'Commercial Product',
          field: 'CommercialProductName',
          filter: 'agTextColumnFilter',
          sort: 'asc',
        },
        {
          headerName: 'Risk Class',
          field: 'RiskCodeName',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Sub Risk Class',
          field: 'SubRiskCodeName',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Sales Country',
          field: 'salesCountryType',
          filter: 'agTextColumnFilter',
          minWidth: 170,
        },
      ],
      producerColumnDefs: [
        {
          headerName: 'Producer Code',
          field: 'CompanyCode',
          filter: 'agTextColumnFilter',
          sort: 'asc',
          rowGroup: true,
          hide: true,
        },
        {
          headerName: 'Producer',
          field: 'CompanyName',
        },
        {
          headerName: 'ProducerId',
          field: 'Company_FK',
          hide: true,
        },
        {
          headerName: 'Commercial Product',
          field: 'CommercialProductName',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Risk Class',
          field: 'RiskCodeName',
          filter: 'agTextColumnFilter',
        },
        {
          headerName: 'Sub Risk Class',
          field: 'SubRiskCodeName',
          filter: 'agTextColumnFilter',
        },
      ],
      autoGroupColumnDef: {
        headerName: 'Producer Code',
        field: 'CompanyCode',
        minWidth: 325,
        cellRendererParams: {
          checkbox: (params) => {
            return !!params.node.group
          },
        },
      },
      statisticalData: {
        commercialProducts: {
          icon: 'file-alt',
          label: 'Total Products',
          value: 0,
          tooltipText: 'Products',
        },
        producers: {
          icon: 'file-user',
          label: 'Total Producers',
          value: 0,
          tooltipText: 'Producers',
        },
      },
    }
  },
  computed: {
    showGridOne() {
      // setTimeout(() => {
      //   this.$refs.producerGrid.$refs.grid.autoResizeGrid()
      // }, 100);
      return this.toggleValue === 0;
    },
  },
  watch: {
    commercialProductList: {
      async handler(commercialProducts) {
        const { data } = await commercialProducts;
        let count = 0;

        if (data) {
          count = data.data.length;
        }

        this.statisticalData.commercialProducts.value = count;
      },
      deep: true,
      immediate: true,
    },
    producersList: {
      async handler(producers) {
        let count = 0;

        if (producers) {

          let producerList = [];

          producers.forEach((item) => {
            producerList.push(item.Company_FK);
          });

          producerList = _.uniq(producerList);

          count = producerList.length;
        }

        this.statisticalData.producers.value = count;
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    this.getCommercialProducts();
    this.getProducers();
  },
  methods: {
    getCommercialProducts(isDeleted = 0) {
      const params = { IsDeleted: isDeleted };
      this.commercialProductList = this.$api.commercialProducts.list({ params });

      return this.commercialProductList;
    },
    async getProducers() {
      const { data } = await this.$api.commercialProducts.listByProducers();

      this.producersList = data.data;

      return this.producersList;
    },
    view({data}) {
      // Handle grouped rows
      if (!data) {
        return;
      }

      const id = data.CommercialProduct_PK || data.id;

      this.$router.push({
        name: 'commercial-products.view',
        params: {id}
      }).catch(() => {
      });
    },
    copyCommercialProducts() {
      const selectedRows = this.producerGridApi.getSelectedNodes();

      if (!selectedRows.length) {
        this.notifyWarning('Please select a producer');
        return false;
      }

      if (selectedRows.length > 1) {
        this.notifyWarning('Please select only a single producer');
        return false;
      }

      // Because we know that all producer groups will contain at least one
      // commercial product, we can get the producer (company) id the following way:
      this.selectedProducer = Number(selectedRows[0].allLeafChildren[0].data.Company_PK)

      this.openModal('transferModal');
    },
    modalUpdated(modalName) {
      if (modalName === 'transferModal') {
        // This will reload the grid and update the counter
        this.getProducers();
      }

      this.closeModal(modalName);
      this.notifySuccess(
          'The commercial products of the selected Producer were successfully copied'
      )
    },
    openModal(modalName) {
      this.modals[modalName].visible = true;
    },
    closeModal(modalName) {
      this.modals[modalName].visible = false;
    },
    modalError(errorMessage) {
      this.$vs.notify({
        color: 'danger',
        title: 'Error occurred',
        text: errorMessage,
        position: 'top-right'
      })
    },
  },
}

</script>

<style lang="scss">
.create-commercial-product-action {
  width: 215px;
}

.statistical-box {
  color: black;
  border: 1px solid black;
  padding: 10px;
  text-align: center;
  width: 120px;
  border-radius: 5px;

  .stat-number {
    font-weight: bold;
    font-size: 18px;
  }
}
</style>
